import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import ErrorWarningIcon from "components/common/InputErrorWarningIcon";
import { useToast } from "contexts/ToastContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useCelleraTheme } from "theme/useCelleraTheme";
import * as yup from "yup";

interface PatientRequestFormData {
  email: string;
}

interface PatientRequestModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (email: string) => Promise<void>;
}

const patientRequestSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required("This field is required.")
    .email("Please enter a valid email address.")
    .matches(
      /^[\x20-\x7E]+$/,
      "Email must contain only ASCII printable characters"
    )
    .max(64, "Email must be 64 characters or less"),
});

function PatientRequestModal({
  open,
  onClose,
  onSubmit,
}: PatientRequestModalProps) {
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<PatientRequestFormData>({
    resolver: yupResolver(patientRequestSchema),
    mode: "onChange",
    defaultValues: { email: "" },
  });

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  }, [isSubmitting, onClose, reset]);

  const handleFormSubmit = useCallback(
    async (data: PatientRequestFormData) => {
      try {
        setIsSubmitting(true);
        await onSubmit(data.email);
        showSuccess(
          "Request Sent",
          "Patient data access request has been sent successfully."
        );
        handleClose();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to send request. Please try again.";
        showError("Request Failed", message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, showSuccess, showError, handleClose]
  );

  const styles = useMemo(
    () => ({
      buttonBase: {
        minWidth: "150px",
        textTransform: "none",
        padding: "8px 24px",
        borderRadius: "24px",
        fontSize: "16px",
      },
    }),
    []
  );

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      slotProps={{
        paper: {
          sx: { borderRadius: 2, minWidth: "400px" },
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            fontSize: "24px",
            paddingBottom: "16px",
          }}
        >
          Patient Data Request
        </Typography>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: "0.5px",
            backgroundColor: colors.background.grayDivider,
            flex: "none",
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        />
      </DialogTitle>

      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
          sx={{ pt: 1 }}
        >
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label="Profile Email"
                type="email"
                required
                error={!!error}
                helperText={
                  error ? error.message : `${field.value.length}/64 characters`
                }
                disabled={isSubmitting}
                autoFocus
                slotProps={{
                  input: {
                    endAdornment: error && (
                      <ErrorWarningIcon hasError={!!error} />
                    ),
                  },
                  htmlInput: { maxLength: 64 },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 1 },
                }}
              />
            )}
          />

          <DialogActions
            sx={{ padding: "24px 0 0 0", justifyContent: "flex-end" }}
          >
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                ...styles.buttonBase,
                color: colors.text.secondary,
                fontWeight: 500,
                border: `1px solid ${colors.border.light}`,
                "&:hover": { backgroundColor: colors.background.highlight },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || isSubmitting}
              sx={{
                ...styles.buttonBase,
                fontWeight: 600,
                backgroundColor: colors.primary.main,
                color: colors.primary.contrast,
                "&:hover": { backgroundColor: colors.primary.dark },
                "&:disabled": {
                  backgroundColor: colors.border.main,
                  color: colors.text.disabled,
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Request"
              )}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default PatientRequestModal;
